import axios from "axios";
import dayjs from "@shared/dayjs";

// NOTE: Leaderboards has no date-range resolver of its own. It used to wrap
// `resolveDateRange` and shift `last_N_days` forward a day, because the shared
// resolver returned `today - N .. today` (N+1 inclusive days). The shared
// resolver now returns exactly N days, so that shift became a double
// correction and produced N-1 day windows here. Use `resolveDateRange`
// directly — it is the single source of truth for every report.

export const getRollupsByDate = async ({ from, to, signal }) => {
  const params = {
    min_date: from,
    max_date: to,
  };

  const { data } = await axios.get("/leaderboards", { params, signal });

  // TODO: do some of the formatting here as far as dividing goals, etc
  return data.users?.map(formatUser) || [];
};

const formatUser = (user) => ({
  ...user,
  avatar_url: user["avatar_url"]?.length > 3 ? user["avatar_url"] : null,
});

export const getRanksByDate = async ({ from, to, activity, userId, signal }) => {
  if (!activity || !userId) {
    return {};
  }
  const params = {
    min_date: from,
    max_date: to,
    metric: activity,
    user_id: userId,
  };

  const { data } = await axios.get("/leaderboards/ranking", { params, signal });

  return {
    brivityRank: data["global_rank"],
    placeRank: data["place_rank"],
  };
};

export const makeDateRangeFromPhrase = (year, timeFrame) => {
  const { from, to } = timeFrameConverters[timeFrame](year, timeFrame);

  return {
    from: from.toString(),
    to: to.toString(),
  };
};

const timeFrameConverters = {
  today: () => {
    const from = dayjs().startOf("day");

    return {
      from,
      to: from.endOf("day"),
    };
  },
  yesterday: () => {
    const from = dayjs().subtract(1, "day").startOf("day");

    return {
      from,
      to: from.endOf("day"),
    };
  },
  "this-week": () => ({
    from: dayjs().startOf("isoWeek"),
    to: dayjs().endOf("isoWeek"),
  }),
  "last-7-days": () => ({
    from: dayjs().subtract(6, "days").startOf("day"),
    to: dayjs().endOf("day"),
  }),
  "last-week": () => {
    const from = dayjs().subtract(1, "week").startOf("isoWeek");

    return {
      from,
      to: from.endOf("isoWeek"),
    };
  },
  "last-14-days": () => ({
    from: dayjs().subtract(14, "days").startOf("day"),
    to: dayjs().endOf("day"),
  }),
  "this-month": () => {
    const to = dayjs();

    return {
      from: to.startOf("month"),
      to: to.endOf("month"),
    };
  },
  "last-30-days": () => ({
    from: dayjs().subtract(30, "days").startOf("day"),
    to: dayjs().endOf("day"),
  }),
  "last-month": () => {
    const from = dayjs().subtract(1, "month").startOf("month");

    return {
      from: from,
      to: from.endOf("month"),
    };
  },
  "last-90-days": () => ({
    from: dayjs().subtract(90, "days").startOf("day"),
    to: dayjs().endOf("day"),
  }),
  ytd: () => ({
    from: dayjs().startOf("year"),
    to: dayjs().endOf("day"),
  }),
  January: (year, timeFrame) => monthRange(year, timeFrame),
  February: (year, timeFrame) => monthRange(year, timeFrame),
  March: (year, timeFrame) => monthRange(year, timeFrame),
  April: (year, timeFrame) => monthRange(year, timeFrame),
  May: (year, timeFrame) => monthRange(year, timeFrame),
  June: (year, timeFrame) => monthRange(year, timeFrame),
  July: (year, timeFrame) => monthRange(year, timeFrame),
  August: (year, timeFrame) => monthRange(year, timeFrame),
  September: (year, timeFrame) => monthRange(year, timeFrame),
  October: (year, timeFrame) => monthRange(year, timeFrame),
  November: (year, timeFrame) => monthRange(year, timeFrame),
  December: (year, timeFrame) => monthRange(year, timeFrame),
  Q1: (year, timeFrame) => quarterRange(year, timeFrame),
  Q2: (year, timeFrame) => quarterRange(year, timeFrame),
  Q3: (year, timeFrame) => quarterRange(year, timeFrame),
  Q4: (year, timeFrame) => quarterRange(year, timeFrame),
};

const quarterRange = (year, quarter) => {
  const quarterNumber = parseInt(quarter.substring(1));
  const from = dayjs(`January 01 ${year}`).add(quarterNumber - 1, "quarters");

  return {
    from,
    to: from.endOf("quarter"),
  };
};

const monthRange = (year, month) => {
  const from = dayjs(`${month} 01 ${year}`);

  return {
    from,
    to: from.endOf("month"),
  };
};

export const CurrentYear = new Date().getFullYear();

export const Activities = [
  {
    fullName: "Calls",
    shortName: "Calls",
    key: "outbound_calls",
  },
  {
    fullName: "Contacts",
    shortName: "Contacts",
    key: "contacts",
  },
  {
    fullName: "Texts",
    shortName: "Texts",
    key: "texts_sent",
  },
  {
    fullName: "Appointments Set",
    shortName: "Appt. Set",
    key: "appointments_set",
  },
  {
    fullName: "Appointments Held",
    shortName: "Appt. Held",
    key: "appointments_held",
  },
  {
    fullName: "Open Houses",
    shortName: "Open Houses",
    key: "open_houses",
  },
  {
    fullName: "Door Knocking",
    shortName: "Door Knocking",
    key: "door_knocking",
  },
  {
    fullName: "Networking Events",
    shortName: "Networking Ev...",
    key: "networking_events",
  },
  {
    fullName: "Circle Prospecting",
    shortName: "Circle Prosp...",
    key: "circle_prospecting_calls",
  },
  {
    fullName: "Canceled/Expired Calls",
    shortName: "Canceled/Exp...",
    key: "canceled_expired_calls",
  },
  {
    fullName: "For Sale By Owner Calls",
    shortName: "FSBO Calls",
    key: "fsbo_calls",
  },
];

export const getOrdinalNumber = (n) => {
  if (!n) {
    return "";
  }

  const lastOne = n % 10;
  const lastTwo = n % 100;

  if (lastTwo > 10 && lastTwo < 20) {
    return "th";
  } else if (lastOne === 1) {
    return "st";
  } else if (lastOne === 2) {
    return "nd";
  } else if (lastOne === 3) {
    return "rd";
  } else {
    return "th";
  }
};

/**
 * Sorts users by the count on the [activity] object and then
 * adds a rank for that activity. The users
 * array must be sorted by the [count] on the [activity] object. Modifies
 * the original objects.
 * @param {Array} sortedUsers
 * @param {String} activity
 * @returns {Array}
 */
export const sortAndRankUsers = (users = [], activity = "") => {
  const sortedUsers = [...users].sort((a, b) => {
    return b[activity].count - a[activity].count;
  });

  let currentRank = 0;
  let previousCount = -1;
  let rankMatches = 0;
  for (const user of sortedUsers) {
    const count = user[activity].count;

    if (!count) {
      user[activity].rank = null;
      continue;
    }

    if (count === previousCount) {
      rankMatches++;
    } else {
      currentRank += rankMatches + 1;
      rankMatches = 0;
    }

    user[activity].rank = currentRank;
    previousCount = count;
  }

  return sortedUsers;
};
